import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';

import { useWebSocketContext } from 'context/WebSocketConnectionContext';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { useNavbarContext } from 'context/NavbarContext';
import { listTextMessages } from 'services/textMessageService';
import { getLocalStorageParseItem, removeLocalStorageItem } from 'utils/localStorageUtils';
import { reorderList } from 'utils/utils';
import { toastConstant } from 'constants/toastmessage';
import { useToast } from 'context/ToastContext';
import useEncryptedURLState from 'hooks/useEncryptedURLState';

export default function useTextMessage(leadDetails, caseDetails, isFromCase, isFromLead, contactId, phoneNumber) {
  const userContext = useUserDetailsContext();
  const websocket = useWebSocketContext();
  const location = useLocation();
  const { addToast } = useToast();
  const navbarContext = useNavbarContext();
  const loadNotificationCount = navbarContext?.notificationData?.loadNotificationCount;

  const [messageThreads, setMessageThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState('');
  const [selectedContact, setSelectedContact] = useState(undefined);
  const [newThreadActive, setNewThreadActive] = useState(false);
  const [activeIndex, setActiveIndex] = useEncryptedURLState('tab', 1, { encrypt: false }); // State for active tab
  const [nextToken, setnextToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPracticeAreas, setSelectedPracticeAreas] = useState([]);
  const [pageCount, setPageCount] = useState(1);
  const [isLastPage, setIsLastPage] = useState(false);

  //To do: update websocket logic
  useEffect(() => {
    //Update Side thread list on websocket
    if (websocket?.textMessageData?.body?.from) {
      const isInboundMessage =
        websocket?.textMessageData?.notification_type === 'inbound_text_message' ||
        websocket?.textMessageData?.notification_type === 'text_message';

      setMessageThreads((pre) => {
        const threadIndex = pre?.findIndex(
          (item) => item?.from_ === websocket?.textMessageData?.body?.from || item?.to === websocket?.textMessageData.body?.from
        );
        if (threadIndex > -1) {
          let updatedList = pre.map((item) => {
            if (item?.from_ === websocket?.textMessageData?.body?.from || item?.to === websocket?.textMessageData.body?.from) {
              return {
                ...item,
                last_message: websocket?.textMessageData?.body?.message_body,
                last_author: isInboundMessage ? 'client' : websocket?.textMessageData?.body?.client_name,
                last_updated: Date.now(),
                unread_reply_count: 1,
                is_read: false, // Mark as unread for new messages
              }; // Create a new object with updated name
            }
            return item; // Return the original item if ID doesn't match
          });
          return reorderList(updatedList, threadIndex, 0);
        } else {
          // Thread not found in current list
          // This can happen when a new message comes to a previously read thread while viewing Unread tab
          // We'll let the next useEffect handle the refresh when activeIndex changes
          return pre;
        }
      });

      // Refresh notification count when a new inbound message arrives
      if (isInboundMessage && loadNotificationCount) {
        // Add a small delay to ensure backend has processed the message
        setTimeout(() => {
          const practiceAreas = selectedPracticeAreas?.length > 0 ? selectedPracticeAreas : [];
          loadNotificationCount(practiceAreas);
        }, 500); // 500ms delay
      }
    }

    // delivery status Update Logic
    if (websocket?.textMessageData?.body?.SmsSid) {
      setMessageThreads((pre) => {
        return pre.map((item) => {
          if (item?.message_id === websocket?.textMessageData?.body?.SmsSid) {
            return { ...item, last_message_status: websocket?.textMessageData?.body?.SmsStatus }; // Create a new object with updated name
          }
          return item; // Return the original item if ID doesn't match
        });
      });
    }
  }, [websocket, loadNotificationCount, selectedPracticeAreas]);

  useEffect(() => {
    if (contactId && phoneNumber && !isFromCase && !isFromLead) {
      setActiveIndex(0);
      fetchTextMessages(contactId, false, '', { practice_area_ids: [] });
    }
  }, [contactId, phoneNumber, isFromCase, isFromLead]);

  useEffect(() => {
    if (contactId && phoneNumber && !isFromCase && !isFromLead) {
      return;
    }

    if (selectedContact?.contact_id || selectedContact === undefined || selectedContact === '' || selectedPracticeAreas) {
      // Only pass practice_area_ids data if it's not from case or lead
      let data = !isFromCase && !isFromLead ? { practice_area_ids: selectedPracticeAreas } : { practice_area_ids: [] };
      fetchTextMessages(selectedContact?.contact_id, false, activeIndex === 0 ? '' : false, data);

      // Refresh notification count when switching tabs or practice areas change
      if (loadNotificationCount && !isFromCase && !isFromLead) {
        loadNotificationCount(selectedPracticeAreas?.length > 0 ? selectedPracticeAreas : []);
      }
    }
  }, [selectedContact, activeIndex, selectedPracticeAreas, isFromCase, isFromLead, contactId, phoneNumber, loadNotificationCount]);

  useEffect(() => {
    if (messageThreads?.length > 0) {
      let currentContactThreads = messageThreads?.filter((t) => t.contact_id === selectedThread?.contact_id);
      let checkThreadActive = currentContactThreads?.some((c) => c.is_active === true);
      setNewThreadActive(checkThreadActive);
    } else {
      setNewThreadActive(true);
    }
  }, [selectedThread, messageThreads]);

  const fetchTextMessages = async (contactId = false, isNewTread = false, is_read = activeIndex === 0 ? '' : false, data = {}) => {
    try {
      const hasUrlParams = contactId && phoneNumber && !isFromCase && !isFromLead;

      if (hasUrlParams) {
        const threadsResponse = await listTextMessages(
          leadDetails?.case_id || caseDetails?.case_id,
          contactId,
          null,
          15,
          '',
          null,
          'forward',
          false,
          pageCount,
          data
        );

        if (threadsResponse?.data?.text_message_threads?.length > 0) {
          const matchingThread = threadsResponse.data.text_message_threads.find(
            (thread) => thread.contact_id === contactId && (thread.to === phoneNumber || thread.from_ === phoneNumber)
          );

          if (matchingThread) {
            matchingThread.is_read = true;

            handleSelectThread(matchingThread);

            setMessageThreads((prevThreads) => {
              const existingThread = prevThreads.find((thread) => thread.sk === matchingThread.sk);
              if (!existingThread) {
                return [matchingThread, ...prevThreads];
              }
              return prevThreads;
            });
          } else {
            setMessageThreads(threadsResponse.data.text_message_threads);
          }
        } else {
          setMessageThreads([]);
        }
      } else {
        const response = await listTextMessages(
          leadDetails?.case_id || caseDetails?.case_id,
          contactId,
          null,
          15,
          is_read,
          null,
          'forward',
          false,
          pageCount,
          data
        );

        if (response?.data?.text_message_threads?.length) {
          setnextToken(response?.data?.next_token);
          let redirectedData = location?.state?.text_message
            ? location?.state?.text_message
            : getLocalStorageParseItem('stateData')?.text_message;
          let filteredData = response?.data?.text_message_threads?.filter((data) =>
            data?.contact_id === redirectedData?.contact_id && data?.to === redirectedData?.to ? redirectedData?.to : redirectedData?.To
          );
          if (isNewTread) {
            handleSelectThread(response.data.text_message_threads?.[0]);
          } else if (redirectedData && filteredData?.length > 0) {
            handleSelectThread(filteredData[0]);
            removeLocalStorageItem('stateData');
          } else {
            selectedContact && handleSelectThread(response.data.text_message_threads?.[0]);
          }
          setMessageThreads(response.data.text_message_threads);
        } else {
          if (selectedContact) {
            handleSelectThread({
              client_name: { first_name: selectedContact?.first_name, last_name: selectedContact?.last_name },
              contact_id: selectedContact?.contact_id,
              to: selectedContact?.phone,
              phone: selectedContact?.phone,
              from_: userContext?.firmPhoneNumber,
              is_active: true,
              latest_phone_number: selectedContact?.phone,
            });
          }
          setMessageThreads([]);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const loadThreads = () => {
    const isUsingPracticeArea = selectedPracticeAreas?.length > 0;
    if (isLoading) return;
    if (!isUsingPracticeArea) {
      if (nextToken === null || nextToken === undefined || nextToken === '') {
        return;
      }
    } else if (isUsingPracticeArea && isLastPage === true) {
      return;
    }
    setIsLoading(true);
    // Only pass practice_area_ids data if it's not from case or lead
    const data = !isFromCase && !isFromLead ? { practice_area_ids: selectedPracticeAreas } : { practice_area_ids: [] };
    const currentPage = pageCount === 1 ? pageCount + 1 : pageCount;
    listTextMessages(
      leadDetails?.case_id || caseDetails?.case_id,
      selectedContact?.contact_id,
      false,
      15,
      activeIndex === 0 ? '' : false,
      nextToken,
      'forward',
      true,
      currentPage,
      data
    )
      .then((response) => {
        if (!isUsingPracticeArea && response?.data?.text_message_threads?.length) {
          setnextToken(response?.data?.next_token);
          setMessageThreads((prev) => [...prev, ...response.data.text_message_threads]);
        } else if (isUsingPracticeArea) {
          setMessageThreads((prev) => [...prev, ...response.data.text_message_threads]);
          setIsLastPage(response?.data?.is_last_page);
          setPageCount(currentPage + 1);
        } else {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.TEXT_MESSAGE_THREAD_NOT_FOUND);
        }
      })
      .catch((error) => {
        console.log(error);
        addToast(
          false,
          toastConstant.toasterType.ERROR,
          toastConstant.api.FAILED,
          toastConstant.message.TEXT_MESSAGE_THREAD_NOT_FOUND_FAILED
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  function navigateToLatest(contact_id) {
    listTextMessages(caseDetails?.case_id, contact_id, false, 15, '')
      .then((response) => {
        if (response?.data?.text_message_threads?.length) {
          let latestThread = response?.data?.text_message_threads?.find((c) => c.is_active === true);
          handleSelectThread(latestThread);
        } else {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.TEXT_MESSAGE_THREAD_NOT_FOUND);
        }
      })
      .catch((error) => {
        console.log(error);
        addToast(
          false,
          toastConstant.toasterType.ERROR,
          toastConstant.api.FAILED,
          toastConstant.message.TEXT_MESSAGE_THREAD_NOT_FOUND_FAILED
        );
      });
  }

  const handleSelectThread = (thread) => setSelectedThread(thread);

  return {
    messageThreads,
    setMessageThreads,
    selectedThread,
    handleSelectThread,
    selectedContact,
    setSelectedContact,
    newThreadActive,
    setNewThreadActive,
    activeIndex,
    setActiveIndex,
    fetchTextMessages,
    loadThreads,
    navigateToLatest,
    isLoading,
    selectedPracticeAreas,
    setSelectedPracticeAreas,
    pageCount,
    setPageCount,
    isLastPage,
    setIsLastPage,
  };
}
